import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { Add, ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
    closestCorners,
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useDroppable,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Task, TaskPerson, TaskStatus, taskService } from "../../../services/taskService";
import { applyLocally, useTaskStore } from "../TaskContext";
import { apiErrorMessage, byBoardOrder, personName, PRIORITY_META, STATUS_META, STATUSES } from "../taskUtils";
import { LinkKind, StaffAvatar, TaskFacts } from "./TaskBits";

const SORT_GAP = 1024;
const SEP = "::";
const laneKey = (lane: string, status: TaskStatus) => `${lane}${SEP}${status}`;
const splitKey = (key: string) => {
    const [lane, status] = key.split(SEP);
    return { lane, status: status as TaskStatus };
};

interface Lane {
    key: string; // a user id, "none", or "all"
    title: string;
}

interface TaskBoardProps {
    tasks: Task[];
    byPerson: boolean;
    activeId: string | null;
    doneCollapsed: boolean;
    onToggleDone: () => void;
    onOpen: (taskId: string) => void;
    onOpenLink: (kind: LinkKind, id: string) => void;
    onAdd: (status: TaskStatus, assigneeId?: string | null) => void;
}

/** The board's cards in reading order (column by column) — for ↑ ↓. */
export function boardOrder(tasks: Task[], byPerson: boolean, doneCollapsed: boolean, people: Map<string, TaskPerson>): Task[] {
    const lanes = buildLanes(tasks, byPerson, people);
    const sorted = [...tasks].sort(byBoardOrder);
    return lanes.flatMap((lane) =>
        STATUSES.filter((s) => !(s === "DONE" && doneCollapsed)).flatMap((s) =>
            sorted.filter((t) => t.status === s && (lane.key === "all" || (t.assignee_user_id || "none") === lane.key)),
        ),
    );
}

function buildLanes(tasks: Task[], byPerson: boolean, people: Map<string, TaskPerson>): Lane[] {
    if (!byPerson) return [{ key: "all", title: "" }];
    const keys = new Set(tasks.map((t) => t.assignee_user_id || "none"));
    return Array.from(keys)
        .map((key) => ({ key, title: key === "none" ? "Unassigned" : people.has(key) ? personName(people.get(key)) : "Former staff member" }))
        .sort((a, b) => (a.key === "none" ? 1 : b.key === "none" ? -1 : a.title.localeCompare(b.title)));
}

export const TaskBoard: React.FC<TaskBoardProps> = ({ tasks, byPerson, activeId, doneCollapsed, onToggleDone, onOpen, onOpenLink, onAdd }) => {
    const store = useTaskStore();
    const { peopleById, meId, rightsFor, upsert, reload, toast, patch, tasks: allTasks } = store;
    const byId = useMemo(() => new Map(tasks.map((t) => [t.task_id, t])), [tasks]);
    const lanes = useMemo(() => buildLanes(tasks, byPerson, peopleById), [tasks, byPerson, peopleById]);

    const computed = useMemo(() => {
        const sorted = [...tasks].sort(byBoardOrder);
        const map: Record<string, string[]> = {};
        lanes.forEach((lane) =>
            STATUSES.forEach((s) => {
                map[laneKey(lane.key, s)] = sorted
                    .filter((t) => t.status === s && (lane.key === "all" || (t.assignee_user_id || "none") === lane.key))
                    .map((t) => t.task_id);
            }),
        );
        return map;
    }, [tasks, lanes]);

    const [items, setItems] = useState(computed);
    const [dragging, setDragging] = useState<string | null>(null);
    const origin = useRef<{ container: string; index: number } | null>(null);

    useEffect(() => {
        if (!dragging) setItems(computed);
    }, [computed, dragging]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        // Space picks a card up; Enter is left to open it.
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
            keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space", "Enter"] },
        }),
    );

    const findContainer = (id: string) => (id in items ? id : Object.keys(items).find((k) => items[k].includes(id)));

    const onDragStart = ({ active }: DragStartEvent) => {
        const id = String(active.id);
        const container = findContainer(id)!;
        origin.current = { container, index: items[container].indexOf(id) };
        setDragging(id);
    };

    const onDragOver = ({ active, over }: DragOverEvent) => {
        if (!over) return;
        const id = String(active.id);
        const overId = String(over.id);
        const from = findContainer(id);
        const to = findContainer(overId);
        if (!from || !to || from === to) return;
        setItems((prev) => {
            const target = prev[to];
            const overIndex = target.indexOf(overId);
            let index = target.length;
            if (overIndex >= 0) {
                const translated = active.rect.current.translated;
                const below = translated && translated.top > over.rect.top + over.rect.height / 2;
                index = below ? overIndex + 1 : overIndex;
            }
            return {
                ...prev,
                [from]: prev[from].filter((x) => x !== id),
                [to]: [...target.slice(0, index), id, ...target.slice(index)],
            };
        });
    };

    const onDragEnd = async ({ active, over }: DragEndEvent) => {
        const id = String(active.id);
        const start = origin.current;
        origin.current = null;
        const task = byId.get(id);
        const to = over ? findContainer(String(over.id)) : undefined;
        if (!task || !to || !start) {
            setDragging(null);
            setItems(computed);
            return;
        }
        let list = items[to];
        const overId = String(over!.id);
        if (!(overId in items)) {
            const oldIndex = list.indexOf(id);
            const newIndex = list.indexOf(overId);
            if (oldIndex !== newIndex && newIndex >= 0) list = arrayMove(list, oldIndex, newIndex);
        }
        const next = { ...items, [to]: list };
        setItems(next);
        const index = list.indexOf(id);
        if (to === start.container && index === start.index) {
            setDragging(null);
            return;
        }

        const { lane, status } = splitKey(to);
        const beforeId = list[index - 1] ?? null;
        const afterId = list[index + 1] ?? null;
        const before = beforeId ? byId.get(beforeId) : undefined;
        const after = afterId ? byId.get(afterId) : undefined;
        // Same placement rule as the server, so the card stays put meanwhile.
        const sortOrder =
            before && after
                ? (before.sort_order + after.sort_order) / 2
                : before
                  ? before.sort_order + SORT_GAP
                  : after
                    ? after.sort_order - SORT_GAP
                    : Math.min(0, ...allTasks.filter((t) => t.status === status).map((t) => t.sort_order)) - SORT_GAP;
        upsert({ ...applyLocally(task, { status }), sort_order: sortOrder });
        setDragging(null);

        try {
            const saved = await taskService.move(id, status, beforeId, afterId);
            upsert(saved);
            const newLane = lane === "none" ? null : lane;
            if (byPerson && lane !== "all" && newLane !== task.assignee_user_id) {
                await patch(saved, { assignee_user_id: newLane });
            }
            if (status === "DONE" && task.status !== "DONE" && task.repeat_rule) {
                await reload(true);
                toast({ message: "Done — the next one is on the list.", severity: "success" });
            }
        } catch (err) {
            upsert(task);
            toast({ message: apiErrorMessage(err, "Couldn't move that task."), severity: "error" });
        }
    };

    const draggingTask = dragging ? byId.get(dragging) : undefined;
    const columns = STATUSES.map((s) => (s === "DONE" && doneCollapsed ? "56px" : "minmax(250px, 1fr)")).join(" ");

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={() => {
                setDragging(null);
                setItems(computed);
            }}
        >
            <Box sx={{ overflowX: "auto", pb: 1, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
                <Box sx={{ minWidth: doneCollapsed ? 830 : 1060 }}>
                    {/* Column headers */}
                    <Box sx={{ display: "grid", gridTemplateColumns: columns, gap: 1.5, mb: 1 }}>
                        {STATUSES.map((s) => {
                            const count = lanes.reduce((n, l) => n + (items[laneKey(l.key, s)]?.length || 0), 0);
                            const collapsedDone = s === "DONE" && doneCollapsed;
                            return (
                                <Stack key={s} direction="row" alignItems="center" spacing={1} sx={{ px: 1, minHeight: 32 }}>
                                    {!collapsedDone && (
                                        <>
                                            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: STATUS_META[s].color }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                {STATUS_META[s].label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                {count}
                                            </Typography>
                                            <Box sx={{ flex: 1 }} />
                                        </>
                                    )}
                                    {s === "DONE" ? (
                                        <Tooltip title={doneCollapsed ? "Show Done (last 14 days)" : "Hide Done"}>
                                            <IconButton size="small" onClick={onToggleDone}>
                                                {doneCollapsed ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        !byPerson && (
                                            <Tooltip title={`New task in ${STATUS_META[s].label}`}>
                                                <IconButton size="small" onClick={() => onAdd(s)}>
                                                    <Add fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )
                                    )}
                                </Stack>
                            );
                        })}
                    </Box>

                    {lanes.map((lane) => {
                        const person = peopleById.get(lane.key);
                        const open = STATUSES.filter((s) => s !== "DONE").reduce((n, s) => n + (items[laneKey(lane.key, s)]?.length || 0), 0);
                        return (
                            <Box key={lane.key} sx={{ mb: byPerson ? 2.5 : 0 }}>
                                {byPerson && (
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5, mb: 1 }}>
                                        <StaffAvatar person={person} userId={lane.key === "none" ? null : lane.key} size={26} tooltip={false} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {lane.title}
                                            {lane.key === meId && (
                                                <Typography component="span" variant="subtitle2" color="text.secondary">
                                                    {" "}
                                                    (you)
                                                </Typography>
                                            )}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {open} open
                                            {person?.overdue_count ? (
                                                <Box component="span" sx={{ color: "error.main", fontWeight: 600 }}>
                                                    {" "}
                                                    · {person.overdue_count} overdue
                                                </Box>
                                            ) : null}
                                        </Typography>
                                        <Tooltip title={`New task for ${lane.key === "none" ? "nobody yet" : lane.title}`}>
                                            <IconButton size="small" onClick={() => onAdd("TODO", lane.key === "none" ? null : lane.key)}>
                                                <Add fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                )}
                                <Box sx={{ display: "grid", gridTemplateColumns: columns, gap: 1.5, alignItems: "stretch" }}>
                                    {STATUSES.map((s) => {
                                        const key = laneKey(lane.key, s);
                                        return (
                                            <Column
                                                key={key}
                                                id={key}
                                                ids={items[key] || []}
                                                collapsed={s === "DONE" && doneCollapsed}
                                                tall={!byPerson}
                                                byId={byId}
                                                activeId={activeId}
                                                canEditTask={(t) => rightsFor(t).canEdit}
                                                onOpen={onOpen}
                                                onOpenLink={onOpenLink}
                                                onExpand={onToggleDone}
                                                showAssignee={!byPerson}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
            <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }}>
                {draggingTask ? <CardBody task={draggingTask} lifted showAssignee={!byPerson} onOpenLink={() => undefined} /> : null}
            </DragOverlay>
        </DndContext>
    );
};

interface ColumnProps {
    id: string;
    ids: string[];
    collapsed: boolean;
    tall: boolean;
    byId: Map<string, Task>;
    activeId: string | null;
    canEditTask: (task: Task) => boolean;
    onOpen: (taskId: string) => void;
    onOpenLink: (kind: LinkKind, id: string) => void;
    onExpand: () => void;
    showAssignee: boolean;
}

const Column: React.FC<ColumnProps> = ({ id, ids, collapsed, tall, byId, activeId, canEditTask, onOpen, onOpenLink, onExpand, showAssignee }) => {
    const { setNodeRef, isOver } = useDroppable({ id });
    const status = splitKey(id).status;
    if (collapsed) {
        return (
            <ButtonBase
                ref={setNodeRef}
                onClick={onExpand}
                sx={{
                    borderRadius: 2.5,
                    bgcolor: isOver ? STATUS_META.DONE.soft : "#f3f6f5",
                    border: "1px dashed",
                    borderColor: isOver ? STATUS_META.DONE.color : "transparent",
                    minHeight: tall ? 420 : 90,
                    flexDirection: "column",
                    gap: 1,
                    color: "text.secondary",
                }}
            >
                <Typography variant="caption" sx={{ fontWeight: 700, writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                    Done · {ids.length}
                </Typography>
            </ButtonBase>
        );
    }
    return (
        <SortableContext id={id} items={ids} strategy={verticalListSortingStrategy}>
            <Stack
                ref={setNodeRef}
                spacing={1}
                sx={{
                    p: 1,
                    borderRadius: 2.5,
                    bgcolor: isOver ? STATUS_META[status].soft : "#f3f6f5",
                    outline: isOver ? `2px dashed ${STATUS_META[status].color}55` : "none",
                    minHeight: tall ? 420 : 90,
                    transition: "background-color 120ms",
                }}
            >
                {ids.map((taskId) => {
                    const task = byId.get(taskId);
                    if (!task) return null;
                    return (
                        <SortableCard
                            key={taskId}
                            task={task}
                            active={taskId === activeId}
                            disabled={!canEditTask(task)}
                            onOpen={() => onOpen(taskId)}
                            onOpenLink={onOpenLink}
                            showAssignee={showAssignee}
                        />
                    );
                })}
                {ids.length === 0 && (
                    <Typography variant="caption" color="text.disabled" sx={{ textAlign: "center", py: 2 }}>
                        Drop tasks here
                    </Typography>
                )}
            </Stack>
        </SortableContext>
    );
};

interface CardProps {
    task: Task;
    active?: boolean;
    disabled?: boolean;
    showAssignee: boolean;
    onOpen?: () => void;
    onOpenLink: (kind: LinkKind, id: string) => void;
}

const SortableCard: React.FC<CardProps> = ({ task, active, disabled, showAssignee, onOpen, onOpenLink }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.task_id, disabled });
    const ref = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (active) ref.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }, [active]);
    return (
        <Box
            ref={(node: HTMLDivElement | null) => {
                setNodeRef(node);
                ref.current = node;
            }}
            {...attributes}
            {...listeners}
            onClick={onOpen}
            onKeyDown={(e: React.KeyboardEvent) => {
                listeners?.onKeyDown?.(e);
                if (e.key === "Enter" && !e.defaultPrevented) onOpen?.();
            }}
            aria-roledescription={disabled ? "task" : "draggable task"}
            sx={{
                transform: CSS.Translate.toString(transform),
                transition,
                opacity: isDragging ? 0.35 : 1,
                outline: "none",
                borderRadius: 2,
                "&:focus-visible > *": { boxShadow: "0 0 0 2px #18383E" },
            }}
        >
            <CardBody task={task} active={active} showAssignee={showAssignee} onOpenLink={onOpenLink} draggable={!disabled} />
        </Box>
    );
};

const CardBody: React.FC<CardProps & { lifted?: boolean; draggable?: boolean }> = ({ task, active, lifted, showAssignee, onOpenLink, draggable = true }) => {
    const { today, peopleById } = useTaskStore();
    const assignee = task.assignee_user_id ? peopleById.get(task.assignee_user_id) : undefined;
    const done = task.status === "DONE";
    const edge = task.priority === "URGENT" || task.priority === "HIGH" ? PRIORITY_META[task.priority].color : "transparent";
    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.25,
                pl: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: active ? "#18383E" : "#e3e8e7",
                boxShadow: lifted ? "0 12px 28px rgba(16,40,44,0.18)" : active ? "0 0 0 1px #18383E" : "0 1px 0 rgba(16,40,44,0.04)",
                cursor: lifted ? "grabbing" : draggable ? "grab" : "pointer",
                position: "relative",
                overflow: "hidden",
                transform: lifted ? "rotate(1.5deg)" : "none",
                bgcolor: "background.paper",
                "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: edge },
                "&:hover": { borderColor: active ? "#18383E" : "#c9d3d1" },
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 550,
                    lineHeight: 1.35,
                    color: done ? "text.secondary" : "text.primary",
                    textDecoration: done ? "line-through" : "none",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    wordBreak: "break-word",
                }}
            >
                {task.title}
            </Typography>
            <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mt: 0.75 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TaskFacts task={task} today={today} onOpenLink={onOpenLink} />
                </Box>
                {showAssignee && <StaffAvatar person={assignee} userId={task.assignee_user_id} size={24} />}
            </Stack>
        </Paper>
    );
};
